import { Box } from '@mui/joy';
import { CSSProperties, ReactNode, useEffect, useRef } from 'react';
import { default as InfiniteScrollBase } from 'react-infinite-scroll-component';

import { Loader } from 'core/elements';
import { Position } from 'core/enums';

interface IProps {
  height: string | number;
  data: unknown[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  children: ReactNode;
  style?: CSSProperties;
}

export const InfiniteScroll = ({
  height,
  data,
  hasNextPage,
  fetchNextPage,
  children,
  style,
}: IProps) => {
  const loaderRef = useRef(null);

  useEffect(() => {
    if (!hasNextPage || !fetchNextPage) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        threshold: 0.2,
      }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  return (
    <InfiniteScrollBase
      dataLength={data.length}
      next={fetchNextPage}
      hasMore={hasNextPage}
      loader={
        <Box ref={loaderRef}>
          <Loader alignItems={Position.TOP} />
        </Box>
      }
      height={height}
      scrollThreshold={0.8}
      style={style}
    >
      {children}
    </InfiniteScrollBase>
  );
};
